// In-house HTTP client — a minimal, dependency-free stand-in for axios over the
// browser's native fetch(). Built to retire the `axios` npm dependency after the
// 2026-03-31 axios supply-chain compromise (see memory/feedback_axios_security.md):
// a 100M-download package that was already weaponized once is a risk we don't need
// to carry when our usage is this basic. No third-party code in the request path =
// no axios supply-chain exposure, ever.
//
// It replicates ONLY the axios surface this codebase actually uses (verified across
// all 54 call-site files): `get/post/put/patch/delete`, an axios-shaped success
// response (`{ data, status, statusText, headers }`) and — critically — an
// axios-shaped THROWN error (`err.response = { data, status, ... }`), plus `params`,
// `timeout`, `signal` (AbortController), `responseType: 'blob'` (PDF/xlsx downloads),
// and FormData bodies (left unserialized so the browser sets the multipart boundary).
//
// Deliberately NOT implemented (unused here): interceptors, axios.create instances,
// CancelToken (we use AbortController), transformRequest/Response, XSRF cookie auto-
// attach (auth is a manual `Authorization: Bearer` header per call). If a new call
// site needs one of these, add it here — do not reach back for axios.

export interface HttpRequestConfig {
  headers?: Record<string, string>;
  params?: Record<string, unknown>;
  signal?: AbortSignal;
  timeout?: number;
  responseType?: 'json' | 'blob' | 'text';
  data?: unknown; // delete() body, axios-style
}

export interface HttpResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
  headers: Headers;
  config: HttpRequestConfig;
}

// Mirrors axios's error: `.response` present on a non-2xx (with the parsed body),
// absent on a network failure / abort. Call sites read `err.response?.data`/`.status`.
export class HttpError<T = any> extends Error {
  response?: { data: T; status: number; statusText: string; headers: Headers };
  request?: RequestInit;
  config: HttpRequestConfig;
  code?: string;
  constructor(message: string, config: HttpRequestConfig, code?: string) {
    super(message);
    this.name = 'HttpError';
    this.config = config;
    this.code = code;
  }
}

function hasHeader(headers: Record<string, string>, name: string): boolean {
  const lower = name.toLowerCase();
  return Object.keys(headers).some((k) => k.toLowerCase() === lower);
}

function deleteHeader(headers: Record<string, string>, name: string): void {
  const lower = name.toLowerCase();
  for (const k of Object.keys(headers)) if (k.toLowerCase() === lower) delete headers[k];
}

function buildUrl(url: string, params?: Record<string, unknown>): string {
  if (!params) return url;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    if (Array.isArray(v)) v.forEach((x) => qs.append(k, String(x)));
    else qs.append(k, String(v));
  }
  const s = qs.toString();
  if (!s) return url;
  return url + (url.includes('?') ? '&' : '?') + s;
}

async function parseBody(res: Response, responseType?: string): Promise<unknown> {
  if (responseType === 'blob') return res.blob();
  if (responseType === 'text') return res.text();
  const text = await res.text();
  if (!text) return null; // 204 / empty
  try { return JSON.parse(text); } catch { return text; }
}

function isRawBody(data: unknown): boolean {
  return (
    data instanceof FormData ||
    data instanceof Blob ||
    data instanceof URLSearchParams ||
    data instanceof ArrayBuffer ||
    typeof data === 'string'
  );
}

async function request<T = any>(
  method: string,
  url: string,
  data: unknown,
  config: HttpRequestConfig = {},
): Promise<HttpResponse<T>> {
  const { params, signal, timeout, responseType } = config;
  const headers: Record<string, string> = { ...(config.headers || {}) };
  const init: RequestInit = { method, headers };

  // Abort: fold together the caller's signal and an optional timeout.
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  if (signal || timeout) {
    const ctrl = new AbortController();
    if (signal) {
      if (signal.aborted) ctrl.abort();
      else signal.addEventListener('abort', () => ctrl.abort(), { once: true });
    }
    if (timeout) timeoutId = setTimeout(() => ctrl.abort(), timeout);
    init.signal = ctrl.signal;
  }

  // Body. FormData / Blob / etc. are passed through untouched so the browser sets the
  // right Content-Type (and multipart boundary); plain objects are JSON-encoded.
  if (data !== undefined && data !== null && method !== 'GET') {
    if (data instanceof FormData) {
      deleteHeader(headers, 'content-type'); // let the browser add the boundary
      init.body = data;
    } else if (isRawBody(data)) {
      init.body = data as BodyInit;
    } else {
      init.body = JSON.stringify(data);
      if (!hasHeader(headers, 'content-type')) headers['Content-Type'] = 'application/json';
    }
  }

  let res: Response;
  try {
    res = await fetch(buildUrl(url, params), init);
  } catch (e) {
    if (timeoutId) clearTimeout(timeoutId);
    const aborted = e instanceof DOMException && e.name === 'AbortError';
    const err = new HttpError(
      aborted ? 'canceled' : (e as Error)?.message || 'Network Error',
      config,
      aborted ? 'ERR_CANCELED' : 'ERR_NETWORK',
    );
    if (aborted) err.name = 'AbortError'; // match axios/CanceledError call-site checks
    err.request = init;
    throw err;
  }
  if (timeoutId) clearTimeout(timeoutId);

  const body = await parseBody(res, responseType);
  if (!res.ok) {
    const err = new HttpError<T>(`Request failed with status code ${res.status}`, config, 'ERR_BAD_RESPONSE');
    err.response = { data: body as T, status: res.status, statusText: res.statusText, headers: res.headers };
    throw err;
  }
  return { data: body as T, status: res.status, statusText: res.statusText, headers: res.headers, config };
}

const http = {
  get: <T = any>(url: string, config?: HttpRequestConfig) => request<T>('GET', url, undefined, config),
  delete: <T = any>(url: string, config?: HttpRequestConfig) => request<T>('DELETE', url, config?.data, config),
  post: <T = any>(url: string, data?: unknown, config?: HttpRequestConfig) => request<T>('POST', url, data, config),
  put: <T = any>(url: string, data?: unknown, config?: HttpRequestConfig) => request<T>('PUT', url, data, config),
  patch: <T = any>(url: string, data?: unknown, config?: HttpRequestConfig) => request<T>('PATCH', url, data, config),
};

export default http;
